import { logger } from '@/lib';
import type { AddonApp, AddonAppResponse } from '../types';

// Fixed base to resolve urls against; any url that leaves this origin points to a foreign host
const SAME_ORIGIN_PROBE = 'https://addon-apps.invalid';

/**
 * Turns backend responses into addon apps. Drops apps whose url is not a same-origin path
 * or yields no slug, and keeps only the first app per slug. Every dropped app is logged.
 */
export function toAddonApps(responses: AddonAppResponse[]): AddonApp[] {
  const addonApps: AddonApp[] = [];
  const seenSlugs = new Set<string>();

  for (const response of responses) {
    const addonApp = toAddonApp(response);

    if (!addonApp) {
      logger.warn('Skipping addon app with invalid url', { name: response.name, url: response.url });
      continue;
    }

    const isDuplicateSlug = seenSlugs.has(addonApp.slug);
    if (isDuplicateSlug) {
      logger.warn('Skipping addon app with duplicate slug', { name: addonApp.name, slug: addonApp.slug });
      continue;
    }

    seenSlugs.add(addonApp.slug);
    addonApps.push(addonApp);
  }

  return addonApps;
}

// Finds the app for a route slug; case-insensitive so /apps/DIMA works like /apps/dima
export function findAddonAppBySlug(addonApps: AddonApp[], slug: string): AddonApp | undefined {
  const normalizedSlug = slug.toLowerCase();
  return addonApps.find((addonApp) => addonApp.slug === normalizedSlug);
}

/**
 * Only root-relative paths on the same origin may be framed. The url is resolved like the browser
 * does, because it strips tabs/newlines and reads "/\host" as "//host" — prefix checks alone miss that.
 */
export function isAllowedAddonAppUrl(url: unknown): url is string {
  const isRootRelative = typeof url === 'string' && url.startsWith('/');
  if (!isRootRelative) {
    return false;
  }

  try {
    return new URL(url, SAME_ORIGIN_PROBE).origin === SAME_ORIGIN_PROBE;
  } catch {
    return false;
  }
}

// Slug is the last path segment in lower case, ignoring query and hash: "/tools/Calc?x=1" → "calc"
export function toAddonAppSlug(url: string): string {
  const [path = ''] = url.split(/[?#]/);
  const segments = path.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? '';
  return lastSegment.toLowerCase();
}

// Maps one response, or returns null when it cannot be framed safely
function toAddonApp(response: AddonAppResponse): AddonApp | null {
  if (!isAllowedAddonAppUrl(response.url)) {
    return null;
  }

  const slug = toAddonAppSlug(response.url);
  if (!slug) {
    return null;
  }

  return { name: response.name, url: response.url, slug };
}
